import { useEffect, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import Layout from "@/components/Layout";
import SEO from "@/components/SEO";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { CheckCircle2, XCircle, Loader2, KeyRound } from "lucide-react";

export default function ThreePaySuccess() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [status, setStatus] = useState("polling"); // polling | paid | failed | expired
  const [data, setData] = useState(null);
  const attempts = useRef(0);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    if (!sessionId) { setStatus("failed"); return; }

    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      try {
        const { data } = await api.get(`/checkout/3pay/status/${sessionId}`);
        setData(data);
        if (data.payment_status === "paid") {
          setStatus("paid");
          return;
        }
        if (data.status === "expired") { setStatus("expired"); return; }
        if (attempts.current++ < 10) setTimeout(poll, 2000);
        else setStatus("failed");
      } catch {
        if (attempts.current++ < 10) setTimeout(poll, 2000);
        else setStatus("failed");
      }
    };
    poll();
    return () => { cancelled = true; };
  }, []);

  return (
    <Layout>
      <SEO title="Payment confirmed" description="Your Forged in Fire enrollment confirmation." noindex />
      <section className="container-narrow py-24 max-w-2xl">
        {status === "polling" && (
          <div className="text-center" data-testid="threepay-polling">
            <Loader2 className="w-8 h-8 mx-auto animate-spin text-brand-ink" />
            <p className="font-serif text-3xl mt-6 text-brand-ink">Confirming your first payment…</p>
            <p className="text-brand-inkMuted mt-2">This will only take a moment.</p>
          </div>
        )}

        {status === "paid" && (
          <div className="bg-white border border-brand-line p-12 text-center" data-testid="threepay-success">
            <CheckCircle2 className="w-12 h-12 mx-auto text-brand-rust" />
            <h1 className="font-display text-4xl md:text-5xl mt-6 text-brand-ink">You&apos;re in.</h1>
            <p className="text-brand-inkMuted mt-3 leading-relaxed">
              Your first payment of <strong className="text-brand-ink">${data?.amount_monthly?.toFixed?.(2) || "349.00"}</strong> has cleared.
              Full access to <em>Forged in Fire: The Course</em> is yours immediately.
            </p>

            <div className="mt-8 border-y border-brand-line py-6 text-left space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-brand-inkMuted">Payment 1 of 3</span>
                <span className="text-brand-ink font-medium">Paid · ${data?.amount_monthly?.toFixed?.(2) || "349.00"}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-brand-inkMuted">Payment 2 of 3</span>
                <span className="text-brand-inkMuted">Charges automatically in ~30 days</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-brand-inkMuted">Payment 3 of 3</span>
                <span className="text-brand-inkMuted">Charges automatically in ~60 days</span>
              </div>
              <p className="text-xs text-brand-inkMuted pt-2 border-t border-brand-line">
                After the third payment, the plan ends automatically. You will not be charged a fourth month.
              </p>
            </div>

            <div className="mt-10 flex flex-wrap gap-4 justify-center">
              {data?.needs_password && data?.claim_token ? null : (
                <>
                  <button
                    data-testid="threepay-go-to-library"
                    onClick={() => navigate("/library")}
                    className="bg-brand-ink text-white hover:bg-black px-7 py-3 text-sm font-medium tracking-wide transition-colors"
                  >
                    Go to My Library
                  </button>
                  <Link
                    to="/p/prd_b8dca9fc6cf1"
                    className="border border-brand-ink text-brand-ink hover:bg-brand-ink hover:text-white px-7 py-3 text-sm font-medium tracking-wide transition-colors"
                  >
                    Back to the course
                  </Link>
                </>
              )}
            </div>

            {data?.needs_password && data?.claim_token && (
              <ClaimAccountForm
                claimToken={data.claim_token}
                email={data.email}
                onSuccess={(user) => { setUser(user); navigate("/library"); }}
              />
            )}

            {data?.email && !data?.needs_password && (
              <p className="text-xs text-brand-inkMuted mt-8">
                A receipt was sent to <strong className="text-brand-ink">{data.email}</strong>.
              </p>
            )}
          </div>
        )}

        {(status === "failed" || status === "expired") && (
          <div className="bg-white border border-brand-line p-12 text-center" data-testid="threepay-failed">
            <XCircle className="w-12 h-12 mx-auto text-brand-inkMuted" />
            <h1 className="font-display text-3xl mt-6 text-brand-ink">
              {status === "expired" ? "Payment session expired" : "We couldn't confirm your payment yet"}
            </h1>
            <p className="text-brand-inkMuted mt-3">
              If you completed checkout, your access will appear in My Library within a few minutes. Otherwise, you can try again from the course page.
            </p>
            <div className="mt-8 flex gap-4 justify-center">
              <Link to="/p/prd_b8dca9fc6cf1" className="bg-brand-ink text-white hover:bg-black px-7 py-3 text-sm font-medium tracking-wide transition-colors">Back to the course</Link>
              <Link to="/library" className="border border-brand-ink text-brand-ink hover:bg-brand-ink hover:text-white px-7 py-3 text-sm font-medium tracking-wide transition-colors">Check My Library</Link>
            </div>
          </div>
        )}
      </section>
    </Layout>
  );
}

function ClaimAccountForm({ claimToken, email, onSuccess }) {
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);

  async function submit(e) {
    e.preventDefault();
    if (password.length < 8) return toast.error("Password must be at least 8 characters");
    if (password !== confirm) return toast.error("Passwords don't match");
    setSubmitting(true);
    try {
      const { data } = await api.post("/auth/set-password", { token: claimToken, password });
      localStorage.setItem("lg_token", data.token);
      toast.success("Signed in. Welcome.");
      onSuccess(data.user);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not set password. Try again.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="mt-10 border-t border-brand-line pt-8" data-testid="threepay-claim-account">
      <div className="flex items-center gap-3">
        <KeyRound className="w-5 h-5 text-brand-rust" />
        <h2 className="font-serif text-2xl text-brand-ink">One last step — set your password.</h2>
      </div>
      <p className="mt-3 text-sm text-brand-inkMuted leading-relaxed">
        We created your account under <strong className="text-brand-ink">{email}</strong>.
        Choose a password now and we&apos;ll sign you straight into the course.
      </p>
      <form onSubmit={submit} className="mt-5 space-y-4 text-left" data-testid="threepay-claim-form">
        <div>
          <label className="overline text-brand-inkMuted">Password</label>
          <input
            type="password"
            required
            minLength={8}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="At least 8 characters"
            className="w-full bg-transparent border-b border-brand-line py-3 mt-1 text-brand-ink focus:outline-none focus:border-brand-rust"
            data-testid="threepay-claim-password"
          />
        </div>
        <div>
          <label className="overline text-brand-inkMuted">Confirm password</label>
          <input
            type="password"
            required
            minLength={8}
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            className="w-full bg-transparent border-b border-brand-line py-3 mt-1 text-brand-ink focus:outline-none focus:border-brand-rust"
            data-testid="threepay-claim-confirm"
          />
        </div>
        <button
          type="submit"
          disabled={submitting}
          data-testid="threepay-claim-submit"
          className="w-full bg-brand-ink text-white hover:bg-black px-7 py-4 text-sm font-medium tracking-wide transition-colors disabled:opacity-60"
        >
          {submitting ? "Setting…" : "Set password & open the course"}
        </button>
      </form>
    </div>
  );
}

